import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Users as UsersIcon, Shield, User, Plus, Edit, Trash2, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';
import GroupBadge from '../components/GroupBadge';

function TeamManagement({ user }) {
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');

  // Group create/edit dialog state
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6'
  });

  // Delete confirmation dialog
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchGroups();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await authFetch(`${API}/team/employees`);
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await authFetch(`${API}/groups`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await authFetch(`${API}/team/employees/${userId}/role`, {
        method: 'POST',
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success('Role updated successfully');
        fetchEmployees();
      } else {
        toast.error('Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleGroupAssignment = async (userId, groupId) => {
    try {
      const response = await authFetch(`${API}/team/employees/${userId}/group`, {
        method: 'POST',
        body: JSON.stringify({ group_id: groupId || null })
      });

      if (response.ok) {
        toast.success('Group assignment updated');
        fetchEmployees();
        fetchGroups();
      } else {
        toast.error('Failed to update group assignment');
      }
    } catch (error) {
      toast.error('Failed to update group assignment');
    }
  };

  const openCreateGroupDialog = () => {
    setEditingGroup(null);
    setGroupFormData({ name: '', description: '', color: '#3b82f6' });
    setIsGroupDialogOpen(true);
  };

  const openEditGroupDialog = (group) => {
    setEditingGroup(group);
    setGroupFormData({
      name: group.name,
      description: group.description || '',
      color: group.color || '#3b82f6'
    });
    setIsGroupDialogOpen(true);
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();

    if (!groupFormData.name.trim()) {
      toast.error('Group name is required');
      return;
    }

    try {
      const url = editingGroup
        ? `${API}/groups/${editingGroup.group_id}`
        : `${API}/groups`;

      const method = editingGroup ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        body: JSON.stringify(groupFormData)
      });

      if (response.ok) {
        toast.success(editingGroup ? 'Group updated successfully' : 'Group created successfully');
        setIsGroupDialogOpen(false);
        fetchGroups();
        fetchEmployees();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save group');
      }
    } catch (error) {
      toast.error('Failed to save group');
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupId) return;

    try {
      const response = await authFetch(`${API}/groups/${deleteGroupId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Group deleted successfully');
        setIsDeleteDialogOpen(false);
        setDeleteGroupId(null);
        fetchGroups();
        fetchEmployees();
      } else {
        toast.error('Failed to delete group');
      }
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const openDeleteDialog = (groupId) => {
    setDeleteGroupId(groupId);
    setIsDeleteDialogOpen(true);
  };

  const filteredEmployees = selectedGroupFilter === 'all'
    ? employees
    : selectedGroupFilter === 'unassigned'
    ? employees.filter(emp => !emp.group_id)
    : employees.filter(emp => emp.group_id === selectedGroupFilter);

  if (user.role !== 'admin') {
    return (
      <Layout user={user}>
        <div className="text-center py-12">
          <p className="text-foreground-muted">You don't have permission to access this page</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-foreground-muted">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="team-page">
        <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT mb-6">Team Management</h1>

        <Tabs defaultValue="members" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="members">Team Members</TabsTrigger>
            <TabsTrigger value="groups">Groups</TabsTrigger>
          </TabsList>

          {/* Team Members Tab */}
          <TabsContent value="members">
            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
              {/* Group Filter */}
              <div className="mb-6 flex items-center gap-2">
                <Filter className="w-4 h-4 text-foreground-muted" />
                <Label htmlFor="group-filter" className="text-sm text-foreground-muted">Filter by group:</Label>
                <Select value={selectedGroupFilter} onValueChange={setSelectedGroupFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All employees</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {groups.map(group => (
                      <SelectItem key={group.group_id} value={group.group_id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {filteredEmployees.length === 0 ? (
                <div className="text-center py-12">
                  <UsersIcon className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                  <p className="text-foreground-muted">
                    {selectedGroupFilter === 'all' ? 'No employees yet' : 'No employees in this group'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredEmployees.map((employee) => (
                    <div
                      key={employee.user_id}
                      className="bg-slate-50 rounded-xl p-6"
                      data-testid={`employee-${employee.user_id}`}
                    >
                      <div className="flex items-start gap-3 mb-4">
                        <div className="relative flex-shrink-0">
                          {employee.picture ? (
                            <>
                              <img
                                src={employee.picture}
                                alt={employee.name}
                                className="w-12 h-12 rounded-full object-cover border border-slate-200"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextElementSibling.style.display = 'flex';
                                }}
                              />
                              <div className="w-12 h-12 rounded-full bg-primary/10 items-center justify-center hidden">
                                <User className="w-6 h-6 text-primary" />
                              </div>
                            </>
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="w-6 h-6 text-primary" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground-DEFAULT truncate">{employee.name}</h3>
                          <p className="text-sm text-foreground-muted truncate">{employee.email}</p>
                        </div>
                      </div>

                      {/* Group Badge */}
                      <div className="mb-3">
                        <Label className="text-xs text-foreground-muted mb-1 block">Group</Label>
                        {employee.group ? (
                          <GroupBadge group={employee.group} />
                        ) : (
                          <span className="text-xs text-foreground-muted">No group assigned</span>
                        )}
                      </div>

                      {/* Group Assignment Dropdown */}
                      <div className="mb-4">
                        <Label htmlFor={`group-${employee.user_id}`} className="text-xs text-foreground-muted mb-1 block">
                          Assign to group
                        </Label>
                        <Select
                          value={employee.group_id || 'none'}
                          onValueChange={(value) => handleGroupAssignment(employee.user_id, value === 'none' ? null : value)}
                        >
                          <SelectTrigger id={`group-${employee.user_id}`} className="w-full">
                            <SelectValue placeholder="Select group" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No group</SelectItem>
                            {groups.map(group => (
                              <SelectItem key={group.group_id} value={group.group_id}>
                                {group.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Role Management */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {employee.role === 'admin' && <Shield className="w-4 h-4 text-primary" />}
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              employee.role === 'admin'
                                ? 'bg-primary/10 text-primary'
                                : 'bg-slate-200 text-slate-700'
                            }`}
                          >
                            {employee.role}
                          </span>
                        </div>

                        {employee.user_id !== user.user_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handleRoleChange(employee.user_id, employee.role === 'admin' ? 'employee' : 'admin')
                            }
                            className="rounded-full text-xs"
                            data-testid={`toggle-role-${employee.user_id}`}
                          >
                            {employee.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups">
            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground-DEFAULT">Groups</h2>
                <Button onClick={openCreateGroupDialog} className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Create Group
                </Button>
              </div>

              {groups.length === 0 ? (
                <div className="text-center py-12">
                  <UsersIcon className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                  <p className="text-foreground-muted mb-4">No groups yet</p>
                  <Button onClick={openCreateGroupDialog} variant="outline">
                    Create your first group
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groups.map((group) => (
                    <div
                      key={group.group_id}
                      className="bg-slate-50 rounded-xl p-6 border-l-4"
                      style={{ borderLeftColor: group.color }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground-DEFAULT mb-1">{group.name}</h3>
                          <GroupBadge group={group} showIcon={false} />
                        </div>
                      </div>

                      {group.description && (
                        <p className="text-sm text-foreground-muted mb-3">{group.description}</p>
                      )}

                      <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                        <span className="text-sm text-foreground-muted">
                          {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                        </span>

                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditGroupDialog(group)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDeleteDialog(group.group_id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Create/Edit Group Dialog */}
        <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingGroup ? 'Edit Group' : 'Create New Group'}</DialogTitle>
              <DialogDescription>
                {editingGroup ? 'Update the group details below.' : 'Add a new group to organize your team members.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleGroupSubmit}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={groupFormData.name}
                    onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                    placeholder="e.g., Engineering, Marketing, Sales"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={groupFormData.description}
                    onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="color">Color</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="color"
                      type="color"
                      value={groupFormData.color}
                      onChange={(e) => setGroupFormData({ ...groupFormData, color: e.target.value })}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={groupFormData.color}
                      onChange={(e) => setGroupFormData({ ...groupFormData, color: e.target.value })}
                      placeholder="#3b82f6"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsGroupDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingGroup ? 'Update Group' : 'Create Group'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Group</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this group? All members will be unassigned from this group. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteGroup}>
                Delete Group
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default TeamManagement;
